import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, Video, Users, ArrowLeft, Check, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Reveal from "@/components/Reveal";

const fmt = (d) => { if (!d) return "TBA"; try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return d; } };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [going, setGoing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)).catch(() => navigate("/events"));
    if (user) api.get("/me/rsvps").then(({ data }) => setGoing((data || []).includes(id)));
  };
  useEffect(() => { load(); }, [id, user]);

  const toggleRsvp = async () => {
    if (!user) { toast("Please join or sign in to RSVP."); navigate("/login"); return; }
    setBusy(true);
    try {
      if (going) { await api.delete(`/events/${id}/rsvp`); setGoing(false); toast("RSVP cancelled."); }
      else { await api.post(`/events/${id}/rsvp`); setGoing(true); toast.success("You're on the list! See you there."); }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!event) return <div className="min-h-screen bg-mahogany-900" />;
  const isDraft = event.status === "draft";
  const isCancelled = event.status === "cancelled";
  const isCompleted = event.status === "completed" || (event.date || "") < todayStr();
  const deadlinePassed = event.registration_deadline && todayStr() > event.registration_deadline;
  const full = (event.rsvp_count || 0) >= event.capacity && !going;
  const rsvpClosed = isCancelled || isCompleted || deadlinePassed || !event.rsvp_enabled;

  return (
    <div data-testid="event-detail-page" className="bg-mahogany-900">
      <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
        {event.image_url && <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-mahogany-950 via-mahogany-950/40 to-mahogany-950/60" />
        <div className="absolute inset-x-0 bottom-0 max-w-6xl mx-auto px-6 pb-12">
          <Link to="/events" className="inline-flex items-center gap-2 text-cream/70 hover:text-gold text-sm mb-5" data-testid="back-to-events"><ArrowLeft size={16} /> All Events</Link>
          <div className="flex items-center gap-3 mb-3">
            <span className="block text-gold uppercase tracking-[0.2em] text-xs">{event.type}</span>
            {isDraft && <span className="text-[10px] uppercase tracking-wider text-caramel border border-caramel/40 rounded-full px-2 py-0.5" data-testid="draft-preview-badge">Draft Preview</span>}
            {isCancelled && <span className="text-[10px] uppercase tracking-wider text-red-300 border border-red-400/40 rounded-full px-2 py-0.5">Cancelled</span>}
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl font-light text-cream">{event.title}</h1>
        </div>
      </div>

      {isDraft && (
        <div className="bg-caramel/15 border-y border-caramel/30" data-testid="draft-banner">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-2 text-caramel text-sm"><Info size={16} /> This is a draft preview — it is not visible to members yet.</div>
        </div>
      )}
      {isCancelled && (
        <div className="bg-red-500/10 border-y border-red-400/30" data-testid="cancelled-banner">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-2 text-red-200 text-sm"><AlertTriangle size={16} /> This gathering has been cancelled.</div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="overline mb-4">About This Gathering</p>
            <p className="text-cream/75 text-lg leading-relaxed font-light whitespace-pre-line">{event.description}</p>
            {event.instructions && (
              <div className="mt-8" data-testid="event-instructions-block">
                <p className="overline mb-2">Good To Know</p>
                <p className="text-cream/70 leading-relaxed whitespace-pre-line">{event.instructions}</p>
              </div>
            )}
            {event.book && (
              <div className="mt-10 flex items-center gap-6 bg-mahogany-950 gold-frame rounded-sm p-6" data-testid="event-associated-book">
                <img src={event.book.cover_url} alt={event.book.title} className="w-20 rounded-sm" />
                <div>
                  <p className="overline mb-1">Featured Book</p>
                  <p className="font-serif text-2xl text-cream">{event.book.title}</p>
                  <p className="text-caramel">by {event.book.author}</p>
                </div>
              </div>
            )}
          </Reveal>
        </div>
        <div>
          <Reveal delay={0.1}>
            <div className="bg-mahogany-950 gold-frame rounded-sm p-8 sticky top-28" data-testid="rsvp-card">
              <div className="space-y-4 mb-7 text-cream/80">
                <p className="flex items-start gap-3"><Calendar size={18} className="text-gold mt-0.5" strokeWidth={1.5} /> {fmt(event.date)}</p>
                {event.time && <p className="flex items-start gap-3"><Clock size={18} className="text-gold mt-0.5" strokeWidth={1.5} /> {event.time}{event.end_time ? ` – ${event.end_time}` : ""}</p>}
                {event.is_virtual ? (
                  <p className="flex items-start gap-3"><Video size={18} className="text-gold mt-0.5" strokeWidth={1.5} /> Virtual Event{going && event.virtual_link ? <><br /><a href={event.virtual_link} target="_blank" rel="noreferrer" className="text-fuchsia break-all">{event.virtual_link}</a></> : ""}</p>
                ) : (
                  (event.location || event.city) && <p className="flex items-start gap-3"><MapPin size={18} className="text-gold mt-0.5" strokeWidth={1.5} /> <span>{event.location}{event.address ? <><br />{event.address}</> : ""}{(event.city || event.state) ? <><br />{[event.city, event.state].filter(Boolean).join(", ")}</> : ""}</span></p>
                )}
                {event.rsvp_enabled && <p className="flex items-start gap-3"><Users size={18} className="text-gold mt-0.5" strokeWidth={1.5} /> {event.rsvp_count || 0} of {event.capacity} spots filled</p>}
              </div>
              {event.registration_deadline && !deadlinePassed && !isCancelled && !isCompleted && (
                <p className="text-cream/40 text-xs mb-4">RSVP by {fmt(event.registration_deadline)}</p>
              )}
              <button onClick={toggleRsvp} disabled={busy || (full && !rsvpClosed) || rsvpClosed} data-testid="rsvp-button"
                className={going ? "btn-secondary w-full disabled:opacity-60" : "btn-primary w-full disabled:opacity-50"}>
                {isCancelled ? "Event Cancelled"
                  : isCompleted ? "This Gathering Has Passed"
                  : !event.rsvp_enabled ? "RSVPs Not Open"
                  : deadlinePassed ? "Registration Closed"
                  : full ? "Fully Booked"
                  : going ? <span className="flex items-center gap-2 justify-center"><Check size={16} /> You're Going — Cancel?</span>
                  : "RSVP Now"}
              </button>
              {!user && !rsvpClosed && <p className="text-cream/50 text-xs text-center mt-4">Free members only. <Link to="/join" className="text-fuchsia">Join the room →</Link></p>}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
